import { LanguagesEnum } from '@/api/enums';
import { equal, handleAxiosError } from '@/api/helpers';
import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import { client } from '@/client';
import {
  Account,
  AccountActionsEnum,
  AccountCourseDelivery,
  AccountFormState,
  AccountTypeEnum,
} from '@/client/accounts';
import { File } from '@/client/files';
import { MailSystemType } from '@/client/sender-profiles';
import { Actions, Subjects, SystemRoles } from '@/client/users';
import {
  LoadingStatuses,
  RedirectPaths,
  RedirectPathsEnum,
} from '@/common/constants';
import { AppContext } from '@/common/context/AppContext';
import { TranslationFunctionType } from '@/common/types';
import { AccountForm } from '@/components/accounts/forms';
import { useAccount, useSaveAccount, useUpdateAccounts } from '@/hooks/query';
import { useAppDispatch, useAppSelector } from '@/hooks/store';
import { usePermission } from '@/hooks/usePermission';
import { useToast } from '@/hooks/useToast';
import {
  selectCurrentAccount,
  setCurrentAccount,
} from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { FlexContainer } from '@/ui/styled-ui';
import { queryStateConverter, showItSupport } from '@/utils/helpers';
import { AxiosError } from 'axios';
import { omit } from 'lodash';
import { MenuItem } from 'primereact/menuitem';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AccountHeader } from './AccountHeader';

const getPathItems = (
  account: Account | undefined,
  currentAccount: Account | null,
  action: string | undefined,
  t: TranslationFunctionType,
  returnUrl?: string,
): MenuItem[] => {
  const pathItems: MenuItem[] = [];

  if (currentAccount && currentAccount.isSystem) {
    pathItems.push(
      {
        label: currentAccount?.name,
        url: RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](currentAccount?.id),
        template: AppBreadCrumbTemplate,
      },
      {
        label: t('accounts'),
        url: returnUrl || RedirectPaths[RedirectPathsEnum.ACCOUNTS](),
        template: AppBreadCrumbTemplate,
      },
    );

    if (account) {
      if (action !== AccountActionsEnum.CREATE) {
        pathItems.push({
          label: account?.name,
          url: !currentAccount?.isSystem
            ? RedirectPaths[RedirectPathsEnum.ACCOUNT](account?.id)
            : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](account?.id),
          template: AppBreadCrumbTemplate,
        });
      }
    }

    if (action === AccountActionsEnum.CREATE) {
      pathItems.push({
        label: t('account.createNew'),
        url: RedirectPaths[RedirectPathsEnum.CREATE_ACCOUNT](),
        template: AppBreadCrumbTemplate,
      });
    }
  } else if (currentAccount && !currentAccount.isSystem) {
    pathItems.push({
      label: currentAccount?.name,
      url: RedirectPaths[RedirectPathsEnum.ACCOUNT](currentAccount?.id),
      template: AppBreadCrumbTemplate,
    });
  }

  return pathItems;
};

export const CreateUpdateAccountPage = () => {
  const { t } = useTranslation();
  const { triggerAccountsLengthChange } = useContext(AppContext);
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { pathname } = location;
  const toast = useToast();
  const { cannot } = usePermission();
  const dispatch = useAppDispatch();
  const [isFormDirty, setIsFormDirty] = useState(false);
  const submitFormRef = useRef<(() => Promise<boolean | void>) | null>(null);

  const returnUrl = (location.state as { from?: string })?.from;

  const [showSystemSettings, setShowSystemSettings] = useState(false);

  const { account, isLoading, refetch } = useAccount({
    accountId: id,
  });

  const currentAccount = useAppSelector(selectCurrentAccount);
  const currentUser = useAppSelector(selectCurrentUser);

  const addAccount = useSaveAccount();
  const updateAccount = useUpdateAccounts();

  const [preselectedLogo, setPreselectedLogo] = useState<File>({} as File);
  const [preselectedGuidelinesFile, setPreselectedGuidelinesFile] =
    useState<File>({} as File);
  const [action, setAction] = useState<AccountActionsEnum>();

  const [initialValues, setInitialValues] = useState<AccountFormState>({
    name: '',
    active: false,
    subdomain: '',
    blockSystemEmails: false,
    isFreeTrial: false,
    freeTrialEndsAt: null,
    freeTrialDeletionDate: null,
    freeTrialFullAccess: false,
    awarenessGoLiveDate: null,
    reseller: null,
    mainAccountOfReseller: null,
    defaultLanguage: LanguagesEnum.EN,
    type: false,
    hubspotId: '',
    hubspotDomains: [],
    courseDelivery: AccountCourseDelivery.CP_APP,
    courseDueDaysCheck: false,
    courseDueDays: 14,
    notifyManagerForOverdueCourses: false,
    managerReport: false,
    managerReportFrequency: undefined,
    courseMinimumScore: null,
    courseReTrainGracePeriodDays: 180,
    courseMinimumScoreCheck: false,
    showItSupport: false,
    companyLogo: undefined,
    guidelinesFile: undefined,
    itSupportSectionTitle: t('account.it.supportContact'),
    itSupportName: '',
    itSupportTitle: '',
    itSupportEmail: '',
    itSupportPhone: '',
    mailSystem: null,
    sendingProfileType: undefined,
    isPhishingSenderWhitelisted: false,
    enableUserflow: false,
    companyDescription: null,
    allowPosthogTracking: false,
    linkedInDescription: null,
    timezone: 'Europe/Copenhagen',
  });

  useEffect(() => {
    if (pathname.includes(AccountActionsEnum.CREATE)) {
      setAction(AccountActionsEnum.CREATE);
      setInitialValues({
        ...initialValues,
        active: true,
        courseDueDaysCheck: true,
      });
      return;
    } else {
      setAction(AccountActionsEnum.UPDATE);
    }

    if (!account && !id && currentAccount) {
      setInitialValues({
        id: currentAccount?.id,
        name: currentAccount?.name,
        active: currentAccount?.active,
        subdomain: currentAccount.subdomain,
        blockSystemEmails: currentAccount?.blockSystemEmails ?? false,
        isFreeTrial: !!currentAccount?.freeTrialEndsAt,
        freeTrialEndsAt: currentAccount?.freeTrialEndsAt,
        freeTrialDeletionDate: currentAccount?.freeTrialDeletionDate ?? null,
        freeTrialFullAccess: currentAccount?.freeTrialFullAccess ?? false,
        awarenessGoLiveDate: currentAccount?.awarenessGoLiveDate ?? null,
        reseller: currentAccount?.reseller,
        mainAccountOfReseller: currentAccount?.mainAccountOfReseller,
        defaultLanguage: currentAccount?.defaultLanguage,
        type: currentAccount?.type === AccountTypeEnum.DEMO,
        hubspotId: currentAccount?.hubspot?.id ?? '',
        hubspotDomains: [
          currentAccount?.hubspot?.primaryDomain || '',
          ...(currentAccount?.hubspot?.additionalDomains || []),
        ].filter(Boolean),
        courseDelivery: currentAccount?.courseDelivery,
        courseDueDaysCheck: !!currentAccount?.courseDueDays,
        courseDueDays: currentAccount?.courseDueDays,
        notifyManagerForOverdueCourses:
          currentAccount?.meta?.notifyManagerForOverdueCourses,
        managerReport: currentAccount?.managerReport,
        managerReportFrequency: currentAccount?.managerReportFrequency,
        courseMinimumScoreCheck: !!currentAccount?.courseMinimumScore,
        courseMinimumScore: currentAccount?.courseMinimumScore ?? null,
        courseReTrainGracePeriodDays:
          currentAccount?.courseReTrainGracePeriodDays ?? null,
        companyLogo: currentAccount.companyLogo?.id ?? undefined,
        guidelinesFile: currentAccount.guidelinesFile?.id ?? undefined,
        showItSupport: showItSupport(currentAccount.branding),
        itSupportSectionTitle:
          currentAccount?.branding?.itSupportSectionTitle ??
          t('account.it.supportContact'),
        itSupportName: currentAccount?.branding?.itSupportName,
        itSupportTitle: currentAccount?.branding?.itSupportTitle,
        itSupportEmail: currentAccount?.branding?.itSupportEmail,
        itSupportPhone: currentAccount.branding?.itSupportPhone,
        mailSystem:
          currentAccount?.mailSystem === MailSystemType.THIRD_PARTY_GATEWAY
            ? MailSystemType.MICROSOFT_365
            : currentAccount?.mailSystem ?? null,
        sendingProfileType: currentAccount?.sendingProfileType,
        isPhishingSenderWhitelisted:
          currentAccount?.isPhishingSenderWhitelisted ?? false,
        enableUserflow: currentAccount?.enableUserflow,
        companyDescription: currentAccount?.companyDescription,
        allowPosthogTracking: currentAccount?.allowPosthogTracking ?? false,
        linkedInDescription: currentAccount?.linkedInDescription,
        timezone: currentAccount?.settings?.timezone ?? null,
      });

      if (currentAccount.companyLogo) {
        setPreselectedLogo(currentAccount.companyLogo);
      }
      if (currentAccount.guidelinesFile) {
        setPreselectedGuidelinesFile(currentAccount.guidelinesFile);
      }
    }

    if (account) {
      setInitialValues({
        id: account?.id,
        name: account?.name,
        active: account?.active,
        subdomain: account.subdomain,
        blockSystemEmails: account?.blockSystemEmails ?? false,
        isFreeTrial: !!account?.freeTrialEndsAt,
        freeTrialEndsAt: account?.freeTrialEndsAt,
        freeTrialDeletionDate: account?.freeTrialDeletionDate ?? null,
        freeTrialFullAccess: account?.freeTrialFullAccess ?? false,
        awarenessGoLiveDate: account?.awarenessGoLiveDate ?? null,
        reseller: account?.reseller,
        mainAccountOfReseller: account?.mainAccountOfReseller,
        defaultLanguage: account?.defaultLanguage,
        type: account?.type === AccountTypeEnum.DEMO,
        hubspotId: account?.hubspot?.id ?? '',
        hubspotDomains: [
          account?.hubspot?.primaryDomain || '',
          ...(account?.hubspot?.additionalDomains || []),
        ].filter(Boolean),
        courseDelivery: account?.courseDelivery,
        courseDueDaysCheck: !!account?.courseDueDays,
        courseDueDays: account?.courseDueDays,
        notifyManagerForOverdueCourses:
          account?.meta?.notifyManagerForOverdueCourses,
        managerReport: account?.managerReport,
        managerReportFrequency: account?.managerReportFrequency,
        courseMinimumScoreCheck: !!currentAccount?.courseMinimumScore,
        courseMinimumScore: account?.courseMinimumScore ?? null,
        courseReTrainGracePeriodDays:
          account?.courseReTrainGracePeriodDays ?? null,
        companyLogo: account.companyLogo?.id ?? undefined,
        guidelinesFile: account.guidelinesFile?.id ?? undefined,
        showItSupport: showItSupport(account.branding),
        itSupportSectionTitle:
          account?.branding?.itSupportSectionTitle ??
          t('account.it.supportContact'),
        itSupportName: account?.branding?.itSupportName,
        itSupportTitle: account?.branding?.itSupportTitle,
        itSupportEmail: account?.branding?.itSupportEmail,
        itSupportPhone: account.branding?.itSupportPhone,
        mailSystem:
          account?.mailSystem === MailSystemType.THIRD_PARTY_GATEWAY
            ? MailSystemType.MICROSOFT_365
            : account?.mailSystem ?? null,
        sendingProfileType: account?.sendingProfileType,
        isPhishingSenderWhitelisted:
          account?.isPhishingSenderWhitelisted ?? false,
        enableUserflow: account?.enableUserflow,
        companyDescription: account?.companyDescription,
        allowPosthogTracking: account?.allowPosthogTracking ?? false,
        linkedInDescription: account?.linkedInDescription,
        timezone: account?.settings?.timezone ?? null,
      });

      if (account.companyLogo) {
        setPreselectedLogo(account.companyLogo);
      }
      if (account.guidelinesFile) {
        setPreselectedGuidelinesFile(account.guidelinesFile);
      }
    }
  }, [account, currentAccount]);

  const handleCheckIfSubdomainExists = async (subdomain?: string) => {
    if (!subdomain) return false;

    const accounts = await client.accounts.getAccounts({
      filters: [equal('subdomain', subdomain)],
    });

    return !!accounts.count;
  };

  const handleAccountAction = async (data: AccountFormState, id?: string) => {
    let response: Account;

    const canManageGuidelines =
      currentUser?.role.code === SystemRoles.ADMIN ||
      currentUser?.role.code === SystemRoles.SUPER_ADMIN ||
      currentUser?.role.code === SystemRoles.MULTIPLE_ACCOUNTS_ADMIN ||
      currentUser?.role.code === SystemRoles.DEVELOPER;

    const shouldDisableUserflow =
      initialValues.freeTrialEndsAt !== null && data.isFreeTrial === false;

    const systemAccountPayload = {
      name: data.name,
      subdomain: data.subdomain ? data.subdomain : undefined,
      active: data.active,
      isSSO: data.isSSO,
      reseller: data.reseller ? data.reseller.id : null,
      defaultLanguage: data.defaultLanguage ? data.defaultLanguage : null,
      type: data.type ? AccountTypeEnum.DEMO : AccountTypeEnum.REAL,
      hubspotId: !data.type ? data.hubspotId : undefined,
      hubspotPrimaryDomain: !data.type ? data?.hubspotDomains[0] : undefined,
      hubspotAdditionalDomains: !data.type
        ? data?.hubspotDomains?.slice(1)
        : undefined,
      courseDelivery: data.courseDelivery,
      freeTrialEndsAt: data.isFreeTrial ? data.freeTrialEndsAt : null,
      freeTrialFullAccess: data.isFreeTrial ? data.freeTrialFullAccess : false,
      awarenessGoLiveDate: data.awarenessGoLiveDate ?? null,
      companyLogo: data.companyLogo || null,
      guidelinesFile: canManageGuidelines
        ? data.guidelinesFile || null
        : undefined,
      branding: {
        itSupportSectionTitle: data.itSupportSectionTitle,
        itSupportName: data.itSupportName,
        itSupportTitle: data.itSupportTitle,
        itSupportEmail: data.itSupportEmail,
        itSupportPhone: data.itSupportPhone,
      },
      mailSystem: data.mailSystem ?? null,
      sendingProfileType: data.sendingProfileType,
      isPhishingSenderWhitelisted: data.isPhishingSenderWhitelisted,
      enableUserflow: shouldDisableUserflow ? false : data.enableUserflow,
      companyDescription: data.companyDescription,
      allowPosthogTracking: data.isFreeTrial,
      linkedInDescription: data.linkedInDescription,
      settings: { timezone: data.timezone ?? null },
    };

    const customerAccountPayload = {
      defaultLanguage: data.defaultLanguage ? data.defaultLanguage : null,
      companyLogo: data.companyLogo || null,
      guidelinesFile: canManageGuidelines
        ? data.guidelinesFile || null
        : undefined,
      branding: {
        itSupportSectionTitle: data.itSupportSectionTitle,
        itSupportName: data.itSupportName,
        itSupportTitle: data.itSupportTitle,
        itSupportEmail: data.itSupportEmail,
        itSupportPhone: data.itSupportPhone,
      },
      companyDescription: data.companyDescription,
      settings: { timezone: data.timezone ?? null },
    };

    if (action === AccountActionsEnum.CREATE) {
      response = await addAccount.create({
        ...systemAccountPayload,
      });
      triggerAccountsLengthChange && triggerAccountsLengthChange();
    } else {
      if (!id) return;

      const isCommercialAdmin =
        currentUser?.role.code === SystemRoles.COMMERCIAL_ADMIN;

      const payload = isCommercialAdmin
        ? ({
            ...customerAccountPayload,
            freeTrialEndsAt: data.isFreeTrial ? data.freeTrialEndsAt : null,
            freeTrialFullAccess: data.isFreeTrial
              ? data.freeTrialFullAccess
              : false,
            reseller: data.reseller ? data.reseller.id : null,
            awarenessGoLiveDate: data.awarenessGoLiveDate ?? null,
            mailSystem: data.mailSystem ?? null,
          } as Record<string, unknown>)
        : omit(
            currentAccount?.isSystem ||
              (showSystemSettings && currentUser?.account.isSystem)
              ? systemAccountPayload
              : customerAccountPayload,
            ['account', account?.subdomain ? 'subdomain' : ''],
          );

      response = await updateAccount.update({
        accountId: id,
        updates: payload,
      });
    }

    if (action === AccountActionsEnum.CREATE) {
      navigate(returnUrl || RedirectPaths[RedirectPathsEnum.ACCOUNTS]());
    } else {
      refetch();
    }

    return response;
  };

  const handleSubmit = async (data: AccountFormState) => {
    try {
      if (
        (!initialValues?.subdomain &&
          (await handleCheckIfSubdomainExists(data.subdomain))) ||
        data.subdomain === 'app'
      ) {
        toast?.error(t('toast.error'), t('account.subdomain.exits'));

        return;
      }

      const response =
        account || currentAccount
          ? await handleAccountAction(data, account?.id ?? currentAccount?.id)
          : await handleAccountAction(data);

      if (
        action === AccountActionsEnum.UPDATE &&
        response &&
        response?.id === currentAccount?.id
      ) {
        // Get the current account in case of data that is not send in the patch account response
        const accountData = await client.accounts.getAccount(response.id);
        dispatch(setCurrentAccount(accountData));
      }

      toast?.success(
        t('toast.success'),
        action === AccountActionsEnum.CREATE
          ? t('account.created')
          : t('account.updated'),
      );
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  return (
    <>
      {isLoading ? (
        <FlexContainer direction="column" className="mt-5">
          <ProgressSpinner />
        </FlexContainer>
      ) : (
        <>
          <AppBreadCrumb
            model={getPathItems(account, currentAccount, action, t, returnUrl)}
          />

          <AccountHeader
            setShowSystemSettings={setShowSystemSettings}
            selectedAccount={account}
            isCreate={action === AccountActionsEnum.CREATE}
            isDirty={
              action === AccountActionsEnum.UPDATE ? isFormDirty : undefined
            }
            onSave={
              action === AccountActionsEnum.UPDATE
                ? async () => submitFormRef.current?.()
                : undefined
            }
            isSaving={
              queryStateConverter(updateAccount) === LoadingStatuses.LOADING
            }
          />

          <FlexContainer justify="flex-start">
            <AccountForm
              isCreate={!initialValues?.id}
              isSystem={!!account?.isSystem}
              accountId={account?.id}
              initialValues={initialValues}
              state={queryStateConverter(account ? updateAccount : addAccount)}
              disabled={
                (cannot(Actions.UPDATE, Subjects.ACCOUNTS) ||
                  !currentAccount?.isSystem) &&
                !showSystemSettings
              }
              locked={
                account?.isSystem &&
                currentUser?.role.code !== SystemRoles.DEVELOPER
              }
              commercialAdminMode={
                currentUser?.role.code === SystemRoles.COMMERCIAL_ADMIN
              }
              onSubmit={handleSubmit}
              preselectedLogo={preselectedLogo}
              preselectedGuidelinesFile={preselectedGuidelinesFile}
              selectedAccount={account || currentAccount || undefined}
              onDirtyChange={
                action === AccountActionsEnum.UPDATE
                  ? setIsFormDirty
                  : undefined
              }
              submitRef={
                action === AccountActionsEnum.UPDATE ? submitFormRef : undefined
              }
              onDescriptionSaved={async () => {
                if (id) {
                  refetch();
                }
                if (!id || account?.id === currentAccount?.id) {
                  const accountData = await client.accounts.getAccount(
                    account?.id ?? currentAccount?.id ?? '',
                  );
                  dispatch(setCurrentAccount(accountData));
                }
              }}
            />
          </FlexContainer>
        </>
      )}
    </>
  );
};
